import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Textarea — Flozy Design System
 *
 * Mirrors the Input component's resting / focus / invalid / disabled states
 * so single-line and multi-line text inputs share one visual language.
 *
 * Resting:  white bg, var(--border-strong) hairline, shadow-xs, 14/20 ink text.
 * Focus:    border-primary + 3px primary-tinted ring via color-mix.
 * Invalid:  destructive border + tinted ring (aria-invalid).
 * Disabled: 0.5 opacity, muted bg.
 */
function Textarea({ className, ...props }: React.ComponentProps<"textarea">) {
  return (
    <textarea
      data-slot="textarea"
      className={cn(
        // base — `font-sans` forces the Inter stack instead of the
        // browser's UA textarea font, so the placeholder matches Input.
        "field-sizing-content min-h-16 w-full rounded-md border bg-background text-sm leading-5 font-sans",
        "px-3 py-2 text-foreground placeholder:text-muted-foreground",
        "border-[var(--border-strong)] shadow-xs outline-none",
        "transition-[color,border-color,box-shadow] duration-150",
        "selection:bg-primary/15 selection:text-foreground",
        // disabled
        "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
        "disabled:bg-muted disabled:text-muted-foreground",
        // focus
        "focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-[color-mix(in_oklch,var(--primary)_22%,transparent)]",
        // invalid
        "aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
        // dark
        "dark:bg-[var(--background)]",
        className,
      )}
      {...props}
    />
  );
}

export { Textarea };
